import PropTypes from 'prop-types';
import React from 'react';
import LineItemStatus from 'lineItemStatus';
import InputCell from 'components/LineItemTable/inputCell';
import ItemEvents from 'components/LineItemTable/itemEvents';
import ItemNote from 'components/LineItemTable/itemNote';
import DropDownCell from 'components/LineItemTable/dropDownCell';
import DocumentLink from 'components/LineItemTable/documentLink';
import { EditableTableRow, EditableTableCell } from 'components/EditableTable';
import contractorChargebackItemShape from 'types/contractorChargebackItem';
import { error as parseError } from '../../../../../../../../utils';
import { formatDollars } from '../../../../../../../../utils/lineItemUtils';
import getCanUseDocumentOptions from '../../../../../../../../utils/documents';
import { getSimpleLocalDate } from '../../../../../../../../utils/dateUtils';
import S from 'styles/table.scss';
import Editable from 'mixins/editable';
import documentType from 'types/documentUpload';
import UserSearch from 'components/UserSearch';
import MediaQuery from 'react-responsive';
import TS from './laborTables.scss';
import { CUT_POINT } from 'config/constants';

const CHARGEBACK_STATUSES = ['Pending Review', 'Ready for Accounting', 'Recorded', 'Uncollectable - Reviewed'];
const RESTRICTED_CHARGEBACK_STATUSES = ['Pending Review', 'Ready for Accounting'];
const SIGNED_DOCUMENT_TYPES = ['Signed Documents'];
const CHARGEBACK_REASONS = [
  { label: 'Vendor Debit', value: 'Vendor Debit' },
  { label: 'Compliance', value: 'Compliance' },
  { label: 'Property Damage', value: 'Property Damage' },
  { label: 'Poor Craftsmanship', value: 'Poor Craftsmanship' },
  { label: 'Customer Compensation', value: 'Customer Compensation' },
  { label: 'Estimator Error', value: 'Estimator Error' },
];

class ContractorChargebackRow extends Editable(React.Component) {
  state = { ...this.props.lineItem };

  getChargebackReasons = () => [
    ...CHARGEBACK_REASONS,
    {
      label: this.props.source === 'IFI' ? 'IFI' : 'SPIG',
      value: this.props.source === 'IFI' ? 'IFI' : 'SPIG',
    },
  ];

  handleChange = attr => val => this.setState({ [attr]: this.sanatize(attr, val) });

  handleTotalChange = this.handleChange('total');
  handleNoteChange = this.handleChange('note');
  handlePaymentPlanChange = this.handleChange('payment_plan');
  handleSignedDocumentChange = this.handleChange('signed_document_id');
  handleReasonChange = this.handleChange('reason');

  handleAssigneeChange = val => this.setState({
    assignee_id: this.getAssigneeChangeValue(val, 'employable_id'),
    assignee_type: this.getAssigneeChangeValue(val, 'employable_type'),
  });

  getAssigneeChangeValue = (val, key) => (val ? val[key] : null)

  getIsAccOrAdmin = () => (['accounting', 'admin'].includes(this.props.role));

  getLineItemStatusVal = () => {
    const lineItem = this.state;
    const { onSave } = this.props;
    const isAccOrAdmin = this.getIsAccOrAdmin();
    const isPendingOrReady = RESTRICTED_CHARGEBACK_STATUSES.indexOf(lineItem.status) !== -1;
    const cellReadOnly = this.statusReadOnly(this.props.readOnly, lineItem.status);
    if (isAccOrAdmin) {
      return (<LineItemStatus
        onChange={onSave}
        lineItem={lineItem}
        options={CHARGEBACK_STATUSES}
        cellReadOnly={cellReadOnly}
      />);
    } else if (!isAccOrAdmin && isPendingOrReady) {
      return (<LineItemStatus
        onChange={onSave}
        lineItem={lineItem}
        options={RESTRICTED_CHARGEBACK_STATUSES}
        cellReadOnly={cellReadOnly}
      />);
    }
    return lineItem.status;
  }

  handleSave = () => this.props.onSave(this.state);

  handleDelete = () => this.props.onDelete(this.state.id);

  formatOptions = options => options.map(option => (
    { value: option, label: option }
  ));

  statusReadOnly = (propReadOnly, status) => propReadOnly || (!this.getIsAccOrAdmin() && status === 'Ready for Accounting');

  renderAssigneeCell = () => {
    const lineItem = this.state;
    const cellValue = lineItem.assignee_id ? `${lineItem.assignee_name} - ${lineItem.assignee_type}` : 'In House';
    const initialValues = lineItem.assignee_id ? { [`${lineItem.assignee_type.toLowerCase()}`]: [lineItem.assignee_id] } : {};
    const err = lineItem.errors && lineItem.errors.assignee && parseError('assignee', lineItem.errors);
    const cellProps = { label: 'Company to be Charged', value: cellValue, error: err };
    return (<EditableTableCell
      {...cellProps}
      onChange={this.handleAssigneeChange}
      className={S.largeColumn}
    >
      <UserSearch
        placeholder="Type to search"
        defaultOptions={[{ value: null, label: 'In House' }]}
        employableType={['Estimator', 'Installer', 'IQM']}
        initialValues={initialValues}
        className={S.lineItemSelect}
      />
    </EditableTableCell>);
  }

  renderStatusCell = () => (<EditableTableCell
    display={!this.context.isEditable}
    label="Status"
    value={this.getLineItemStatusVal()}
    className={S.largeColumn}
    cellReadOnly={this.statusReadOnly(this.props.readOnly, this.state.status)}
  />);

  renderTableDesktop = () => {
    const lineItem = this.state;
    const { documents, readOnly } = this.props;
    const rowProps = {
      isNew: lineItem.id < 0,
      onSave: this.handleSave,
      onDelete: this.handleDelete,
      readOnly: this.statusReadOnly(readOnly, lineItem.status),
    };
    const documentOptions = getCanUseDocumentOptions(documents, SIGNED_DOCUMENT_TYPES);
    return (<EditableTableRow key={lineItem.id} id={lineItem.id} deleteDialog {...rowProps}>
      <ItemEvents
        display={!this.context.isEditable}
        label=""
        itemType="contractorChargebackItems"
        lineItem={lineItem}
        dataKeys={['assignee_name', 'assignee_type', 'note', 'total', 'status', 'reason']}
      />
      {this.renderAssigneeCell()}
      <DropDownCell
        attr="signed_document"
        isIdAttr
        label="Signed Document"
        lineItem={lineItem}
        options={documentOptions}
        onChange={this.handleSignedDocumentChange}
        clearable
      >
        <DocumentLink documents={documents} id={lineItem.signed_document_id} />
      </DropDownCell>
      <DropDownCell
        attr="reason"
        label="Chargeback Reason"
        lineItem={lineItem}
        options={this.getChargebackReasons()}
        onChange={this.handleReasonChange}
        clearable
      />
      <InputCell type="number" attr="payment_plan" label="Payment Plan" lineItem={lineItem} onChange={this.handlePaymentPlanChange} cellValue={formatDollars(lineItem.payment_plan)} />
      <InputCell type="number" attr="total" label="Total" lineItem={lineItem} onChange={this.handleTotalChange} cellValue={formatDollars(lineItem.total)} />
      {this.renderStatusCell()}
      <EditableTableCell display={!this.context.isEditable} label="Created" value={getSimpleLocalDate(lineItem.created_at)} />
      <ItemNote display label="Note" onChange={this.handleNoteChange} note={lineItem.note} errors={lineItem.errors} id={lineItem.id} readOnly={readOnly} />
    </EditableTableRow>);
  }

  renderTableMobile = () => {
    const lineItem = this.state;
    const { documents, readOnly } = this.props;
    const rowProps = {
      isNew: lineItem.id < 0,
      onSave: this.handleSave,
      onDelete: this.handleDelete,
      readOnly: this.statusReadOnly(readOnly, lineItem.status),
    };
    const documentOptions = getCanUseDocumentOptions(documents, SIGNED_DOCUMENT_TYPES);
    return (<EditableTableRow key={lineItem.id} id={lineItem.id} deleteDialog {...rowProps}>
      {this.renderAssigneeCell()}
      <DropDownCell
        attr="signed_document"
        isIdAttr
        label="Signed Document"
        lineItem={lineItem}
        options={documentOptions}
        onChange={this.handleSignedDocumentChange}
        clearable
      >
        <DocumentLink documents={documents} id={lineItem.signed_document_id} />
      </DropDownCell>
      <DropDownCell
        attr="reason"
        label="Chargeback Reason"
        lineItem={lineItem}
        options={this.getChargebackReasons()}
        onChange={this.handleReasonChange}
        clearable
      />
      <InputCell type="number" attr="payment_plan" label="Payment Plan" lineItem={lineItem} onChange={this.handlePaymentPlanChange} cellValue={formatDollars(lineItem.payment_plan)} />
      <InputCell type="number" attr="total" label="Total" lineItem={lineItem} onChange={this.handleTotalChange} cellValue={formatDollars(lineItem.total)} />
      {this.renderStatusCell()}
      <EditableTableCell display={!this.context.isEditable} label="Created" value={getSimpleLocalDate(lineItem.created_at)} />
      <ItemNote display label="Note" onChange={this.handleNoteChange} note={lineItem.note} errors={lineItem.errors} id={lineItem.id} readOnly={readOnly} />
      <td>
        <div>
          <table className={TS.inlineTableItem}>
            <tbody className={TS.inlineTableItem}>
              <tr className={TS.iconWrapper}>
                <ItemEvents
                  display={!this.context.isEditable}
                  label=""
                  itemType="contractorChargebackItems"
                  lineItem={lineItem}
                  dataKeys={['assignee_name', 'assignee_type', 'note', 'total', 'status', 'reason']}
                />
              </tr>
            </tbody>
          </table>
        </div>
      </td>
    </EditableTableRow>);
  }

  render = () => (<MediaQuery minWidth={CUT_POINT}>
    {matches => (matches ? this.renderTableDesktop() : this.renderTableMobile())}
  </MediaQuery>);
}

ContractorChargebackRow.contextTypes = { isEditable: PropTypes.bool };

ContractorChargebackRow.propTypes = {
  lineItem: PropTypes.shape(contractorChargebackItemShape),
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onChange: PropTypes.func,
  documents: PropTypes.arrayOf(documentType).isRequired,
  readOnly: PropTypes.bool,
  role: PropTypes.string,
  source: PropTypes.string,
};

ContractorChargebackRow.defaultProps = {
  lineItem: {},
  assignees: [],
  documents: [],
  readOnly: false,
};

export default ContractorChargebackRow;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseAccounting/components/contractorChargebackRow.jsx